import { useEffect, useState, useRef } from "react";
import { LinkPicker, LinkOption } from "../../../../atoms";
import DatePicker from "../DatePicker";
import api from "../../../../../utils/api";
import { API_ENDPOINTS } from "../../../../../config";
import { onKanbansRefresh } from "../../../../../utils/subscriptionEvents";
import "../../../../../styles/documents/modals/text-document-modal.css";
import { useIsMobile } from "../../../../../hooks/useMediaQuery";
import { showToast } from "../../../../../utils/toastManager";

interface Kanban {
  uid: string;
  name: string;
}

interface Job {
  uid: string;
  job_title: string;
  company_name?: string | null;
}

export type ActivityData = {
  title: string;
  category: string;
  startDate: Date | null;
  endDate: Date | null;
  note: string;
  completed: boolean;
  board?: string | null;
  job?: string | null;
};

export interface NewActivityModalProps {
  open: boolean;
  onClose: () => void;
  onCommit: (data: ActivityData) => void;
  currentBoard?: string;
  currentJob?: string;
  availableBoards?: LinkOption[];
  availableJobs?: LinkOption[];
  allowedCategories?: string[];
}

export default function NewActivityModal({
  open,
  onClose,
  onCommit,
  currentBoard,
  currentJob,
  availableBoards: propAvailableBoards,
  availableJobs: propAvailableJobs,
  allowedCategories,
}: NewActivityModalProps) {
  const isMobile = useIsMobile();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Apply');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [note, setNote] = useState('');
  const [completed, setCompleted] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const selectedBoardRef = useRef<string | null>(null);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  
  const [boards, setBoards] = useState<LinkOption[]>(propAvailableBoards || []);
  const [jobs, setJobs] = useState<LinkOption[]>(propAvailableJobs || []);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const ALL_CATEGORIES = [
    'Prep for Interview',
    'Send Thank You',
    'Send Availability',
    'Apply',
    'Follow Up',
    'Prep Cover Letter',
    'Prep Resume',
    'Reach Out',
    'Phone Screen',
    'Phone Interview',
    'On Site Interview',
    'Offer Received',
    'Accept Offer',
    'Decline Offer',
    'Rejected',
    'Email',
    'Meeting',
    'Phone Call',
    'Get Reference',
    'Assignment',
    'Networking Event',
    'Other',
    'Application Withdrawn'
  ];

  const CATEGORIES = allowedCategories && allowedCategories.length > 0 
    ? allowedCategories 
    : ALL_CATEGORIES;

  useEffect(() => {
    if (open && !propAvailableBoards) {
      fetchBoards();
    } else if (propAvailableBoards) {
      setBoards(propAvailableBoards);
    }
  }, [open, propAvailableBoards]);

  useEffect(() => {
    if (propAvailableBoards) return;
    
    const unsubscribe = onKanbansRefresh(() => {
      if (open && !propAvailableBoards) {
        fetchBoards();
      }
    });
    return unsubscribe;
  }, [open, propAvailableBoards]);

  useEffect(() => {
    if (currentBoard && boards.length > 0 && boards.some(board => board.id === currentBoard)) {
      selectedBoardRef.current = currentBoard;
      setSelectedBoard(currentBoard);
    }
  }, [currentBoard, boards]);

  useEffect(() => {
    if (selectedBoard && !propAvailableJobs) {
      fetchJobs(selectedBoard);
    } else if (propAvailableJobs) {
      setJobs(propAvailableJobs);
    } else {
      setJobs([]);
      setSelectedJob(null);
    }
  }, [selectedBoard, propAvailableJobs]);

  const fetchBoards = async () => {
    try {
      setLoadingBoards(true);
      const data = await api.get<Kanban[]>(API_ENDPOINTS.kanbans.list);
      const boardOptions: LinkOption[] = data.map(kanban => ({
        id: kanban.uid,
        label: kanban.name
      }));
      setBoards(boardOptions);
      
      const currentBoard = selectedBoardRef.current;
      if (currentBoard) {
        const boardExists = boardOptions.some(b => b.id === currentBoard);
        if (!boardExists) {
          setSelectedBoard(null);
          selectedBoardRef.current = null;
          setSelectedJob(null);
          setJobs([]);
        }
      }
    } catch (error) {

      setBoards([]);
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchJobs = async (kanbanUid: string) => {
    try {
      setLoadingJobs(true);
      const data = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(kanbanUid));
      const jobOptions: LinkOption[] = data.map(job => ({
        id: job.uid,
        label: job.company_name ? `${job.job_title} at ${job.company_name}` : job.job_title
      }));
      setJobs(jobOptions);
    } catch (error) {

      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleBoardChange = (boardId: string | null) => {
    selectedBoardRef.current = boardId;
    setSelectedBoard(boardId);
    setSelectedJob(null);
  };

  const handleJobChange = (jobId: string | null) => {
    setSelectedJob(jobId);
  };

  useEffect(() => {
    if (!open) return;
    setTitle('');
    const defaultCategory = allowedCategories && allowedCategories.length > 0 
      ? allowedCategories[0] 
      : 'Apply';
    setCategory(defaultCategory);
    setStartDate(new Date());
    setEndDate(null);
    setNote('');
    setCompleted(false);
    if (!currentBoard) {
      setSelectedBoard(null);
    }
    if (!currentJob) {
      setSelectedJob(null);
    }
  }, [open, allowedCategories]);
  
  useEffect(() => {
    if (currentJob && jobs.length > 0 && jobs.some(job => job.id === currentJob)) {
      setSelectedJob(currentJob);
    }
  }, [currentJob, jobs]);

  // Title is marked Required in the form, so gate on it rather than on
  // "is anything filled in" — otherwise Create looks active and does nothing.
  const canCreate = title.trim() !== '';

  const handleCreate = () => {
    if (!canCreate) {
      showToast('Enter a title for the activity', 'error');
      return;
    }
    onCommit({
      title,
      category,
      startDate,
      endDate,
      note,
      completed,
      board: selectedBoard,
      job: selectedJob,
    });
  };

  const createButton = (
    <button
      className="tdm-btn tdm-btn--primary"
      onClick={handleCreate}
      disabled={!canCreate}
      title={canCreate ? undefined : 'Enter a title first'}
    >
      Create
    </button>
  );

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ set end date';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric'
    });
  };

  const formatStartDate = (date: Date | null): string => {
    if (!date) {
      const today = new Date();
      return today.toLocaleDateString('en-US', { 
        month: 'short', 
        day: '2-digit', 
        year: 'numeric'
      });
    }
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric'
    });
  };

  if (!open) return null;

  return (
    <div className="tdm-overlay">
      <div className="tdm-overlay-bg" onClick={onClose} />
      <div className="tdm-modal">
        <div className="tdm-grid">
          <div className="tdm-left">
            <div className="tdm-header">
              <div className="tdm-header-title">Activity Details</div>
              <div className="tdm-header-actions">
                <button
                  type="button"
                  className="tdm-btn tdm-btn--close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="tdm-divider" />
            
            <div className="tdm-content">
              <div className="tdm-field">
                <label className="tdm-label-row">
                  <span>Title</span>
                  <span className="tdm-label-right">
                    <span style={{ color: '#9ca3af' }}>Required</span>
                    <span className="tdm-tooltip-wrapper">
                      <span className="tdm-tooltip-trigger">?</span>
                      <span className="tdm-tooltip">
                        Give your activity a descriptive title that helps you identify it later
                      </span>
                    </span>
                  </span>
                </label>
                <input
                  type="text"
                  className="tdm-input"
                  placeholder="Apply"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="tdm-field">
                <label className="tdm-label-row">
                  <span>Category</span>
                  <span className="tdm-label-right">
                    <span style={{ color: '#9ca3af' }}>Required</span>
                    <span className="tdm-tooltip-wrapper">
                      <span className="tdm-tooltip-trigger">?</span>
                      <span className="tdm-tooltip">
                        Select the type of activity to organize your job search efforts
                      </span>
                    </span>
                  </span>
                </label>
                <div className="tdm-category-chips">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`tdm-chip ${category === cat ? 'active' : ''}`}
                      onClick={() => setCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="tdm-dates">
                <div className="tdm-field">
                  <label className="tdm-label-row">
                    <span>Start Date</span>
                    <span className="tdm-label-right">
                      <span className="tdm-tooltip-wrapper">
                        <span className="tdm-tooltip-trigger">?</span>
                        <span className="tdm-tooltip">
                          When did this activity begin or when is it scheduled to start?
                        </span>
                      </span>
                    </span>
                  </label>
                  <div className="date-picker-wrapper">
                    <button
                      type="button"
                      className="tdm-date-btn"
                      onClick={() => {
                        setShowStartCalendar(!showStartCalendar);
                        setShowEndCalendar(false);
                      }}
                    >
                      {formatStartDate(startDate)}
                    </button>
                    {showStartCalendar && (
                      <DatePicker
                        selectedDate={startDate}
                        onSelect={(date) => {
                          setStartDate(date);
                          setShowStartCalendar(false);
                        }}
                        onClose={() => setShowStartCalendar(false)}
                      />
                    )}
                  </div>
                </div>

                <div className="tdm-field">
                  <label className="tdm-label-row">
                    <span>End Date</span>
                    <span className="tdm-label-right">
                      <span className="tdm-tooltip-wrapper">
                        <span className="tdm-tooltip-trigger">?</span>
                        <span className="tdm-tooltip">
                          When was this activity completed or when is it due? Must be after start date
                        </span>
                      </span>
                    </span>
                  </label>
                  <div className="date-picker-wrapper">
                    <button
                      type="button"
                      className="tdm-date-btn"
                      onClick={() => {
                        setShowEndCalendar(!showEndCalendar);
                        setShowStartCalendar(false);
                      }}
                    >
                      {formatDate(endDate)}
                    </button>
                    {showEndCalendar && (
                      <DatePicker
                        selectedDate={endDate}
                        onSelect={(date) => {
                          setEndDate(date);
                          setShowEndCalendar(false);
                        }}
                        onClose={() => setShowEndCalendar(false)}
                        minDate={startDate || undefined}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="tdm-field">
                <label className="tdm-label-row">
                  <span>Note</span>
                  <span className="tdm-label-right">
                    <span className="tdm-tooltip-wrapper">
                      <span className="tdm-tooltip-trigger">?</span>
                      <span className="tdm-tooltip">
                        Add any additional details, reminders, or follow-up actions for this activity
                      </span>
                    </span>
                  </span>
                </label>
                <textarea
                  className="tdm-textarea"
                  placeholder="i.e: A note about the activity"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="tdm-field">
                <label className="tdm-label-row">
                  <span>Mark as Completed</span>
                  <span className="tdm-label-right">
                    <span className="tdm-tooltip-wrapper">
                      <span className="tdm-tooltip-trigger">?</span>
                      <span className="tdm-tooltip">
                        Check this box if the activity has been completed
                      </span>
                    </span>
                  </span>
                </label>
                <input
                  type="checkbox"
                  checked={completed}
                  onChange={(e) => setCompleted(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>

          <div className="tdm-right">
            <div className="tdm-right-header">
              <div className="tdm-right-title">Create Activity</div>
              {!isMobile && (
                <div className="tdm-header-actions">{createButton}</div>
              )}
            </div>
            <div className="tdm-divider" />

            <div className="activity-info-block">
              <div className="activity-info-text">
                <div className="activity-info-title">Instructions</div>
                <div className="activity-info-description">
                  This activity represents an application for a job opportunity.
                </div>
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <LinkPicker
                label="Board"
                placeholder="Link board"
                options={boards}
                value={selectedBoard}
                onChange={handleBoardChange}
                disabled={loadingBoards}
              />
            </div>

            <div style={{ marginTop: '12px' }}>
              <LinkPicker
                label="Link Job"
                placeholder="Link job"
                options={jobs}
                value={selectedJob}
                onChange={handleJobChange}
                disabled={loadingJobs || !selectedBoard}
              />
            </div>

            {isMobile && (
              <div className="tdm-sheet-actions">{createButton}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

